<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FinestCard from '@/components/FinestCard.vue'
import ValueDisplay from '@/components/ValueDisplay.vue'
import DecisionBadge from '@/pages/banks/my-bank/DecisionBadge.vue'
import EmptyState from '@/components/EmptyState.vue'
import PartnerBankData = App.Data.PartnerBank.PartnerBankData

const props = defineProps<{
  partnerBank: PartnerBankData
}>()

const { t } = useI18n()

const sortedBankCodes = computed(() =>
  [...(props.partnerBank.bank_codes ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
)

const sortedNotes = computed(() =>
  [...(props.partnerBank.notes ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
)

function noteSeverity(noteType: string): 'danger' | 'warn' | 'info' {
  if (noteType === 'absolute_exclusion') return 'danger'
  if (noteType === 'financing') return 'warn'
  return 'info'
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      <FinestCard :title="t('master_data')" icon="pi pi-building-columns">
        <div class="grid gap-x-12 gap-y-6">
          <ValueDisplay :label="t('name')" :value="partnerBank.name" format="string" />
          <ValueDisplay :label="t('short_name')" :value="partnerBank.short_name" format="string" />
          <ValueDisplay :label="t('bic')" :value="partnerBank.bic" format="string" />
          <ValueDisplay :label="t('central_phone')" :value="partnerBank.central_phone" format="string" />
          <ValueDisplay :label="t('valuation_tool')" :value="partnerBank.valuation_tool" format="string" />
          <ValueDisplay :label="t('description')" :value="partnerBank.description" format="string" :lines="3" />
          <div v-if="sortedBankCodes.length">
            <div class="text-surface-400 mb-2 text-xs font-medium tracking-wider uppercase">
              {{ t('bank_codes') }}
            </div>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="code in sortedBankCodes"
                :key="code.id ?? code.bank_code"
                class="bg-surface-100 text-surface-800 rounded-lg px-3 py-1 font-mono text-sm">
                {{ code.bank_code }}
              </span>
            </div>
          </div>
        </div>
      </FinestCard>

      <FinestCard :title="t('address')" icon="pi pi-map-marker">
        <div class="grid gap-x-12 gap-y-6">
          <ValueDisplay :label="t('street')" :value="partnerBank.street" format="string" />
          <ValueDisplay :label="t('zip_code')" :value="partnerBank.zip_code" format="string" />
          <ValueDisplay :label="t('city')" :value="partnerBank.city" format="string" />
          <ValueDisplay :label="t('po_box')" :value="partnerBank.po_box" format="string" />
        </div>
      </FinestCard>

      <FinestCard :title="t('billing_address')" icon="pi pi-receipt">
        <div class="grid gap-x-12 gap-y-6">
          <ValueDisplay :label="t('billing_name')" :value="partnerBank.billing_name" format="string" />
          <ValueDisplay :label="t('street')" :value="partnerBank.billing_street" format="string" />
          <ValueDisplay :label="t('zip_code')" :value="partnerBank.billing_zip_code" format="string" />
          <ValueDisplay :label="t('city')" :value="partnerBank.billing_city" format="string" />
          <ValueDisplay :label="t('po_box')" :value="partnerBank.billing_po_box" format="string" />
          <ValueDisplay :label="t('email')" :value="partnerBank.billing_email" format="string" />
        </div>
      </FinestCard>
    </div>

    <div class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      <FinestCard :title="t('processing_times')" icon="pi pi-clock">
        <div class="grid gap-x-12 gap-y-6">
          <ValueDisplay
            :label="t('initial_feedback_days')"
            :value="partnerBank.initial_feedback_days"
            format="number"
            :suffix="` ${t('days')}`" />
          <ValueDisplay
            :label="t('final_commitment_days')"
            :value="partnerBank.final_commitment_days"
            format="number"
            :suffix="` ${t('days')}`" />
          <ValueDisplay
            :label="t('contract_documents_days')"
            :value="partnerBank.contract_documents_days"
            format="number"
            :suffix="` ${t('days')}`" />
        </div>
      </FinestCard>

      <FinestCard :title="t('settings')" icon="pi pi-cog">
        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-2">
            <span class="text-surface-400 text-xs font-medium tracking-wider uppercase">
              {{ t('receives_bank_newsletter') }}
            </span>
            <DecisionBadge :decision="partnerBank.receives_bank_newsletter" />
          </div>
          <div class="flex flex-col gap-2">
            <span class="text-surface-400 text-xs font-medium tracking-wider uppercase">
              {{ t('broker_agreement_template_available') }}
            </span>
            <DecisionBadge :decision="partnerBank.broker_agreement_template_available" />
          </div>
        </div>
      </FinestCard>
    </div>

    <FinestCard :title="t('notes')" icon="pi pi-info-circle">
      <ul v-if="sortedNotes.length" class="flex flex-col gap-4">
        <li v-for="note in sortedNotes" :key="note.id ?? note.content" class="flex flex-col gap-2">
          <Tag :value="t(`noteType.${note.note_type}`)" :severity="noteSeverity(note.note_type)" class="w-fit" />
          <p class="text-surface-700">{{ note.content }}</p>
        </li>
      </ul>
      <EmptyState v-else :description="t('no_notes')" />
    </FinestCard>
  </div>
</template>

<i18n>
{
  "de": {
    "master_data": "Stammdaten",
    "name": "Name",
    "short_name": "Kurzname",
    "bic": "BIC",
    "central_phone": "Telefon (Zentrale)",
    "valuation_tool": "Bewertungstool",
    "description": "Beschreibung",
    "address": "Anschrift",
    "street": "Straße",
    "zip_code": "PLZ",
    "city": "Ort",
    "po_box": "Postfach",
    "billing_address": "Rechnungsanschrift",
    "billing_name": "Empfänger",
    "email": "E-Mail",
    "reference_bank": "Referenzbank",
    "reference_bank_name": "Bezeichnung",
    "bank_code": "Bankleitzahl",
    "bank_codes": "Bankleitzahlen",
    "no_reference_bank": "Keine Referenzbank hinterlegt.",
    "processing_times": "Bearbeitungsfristen",
    "initial_feedback_days": "Erste Rückmeldung",
    "final_commitment_days": "Verbindliche Zusage",
    "contract_documents_days": "Vertragsunterlagen",
    "days": "Tage",
    "settings": "Einstellungen",
    "receives_bank_newsletter": "Erhält Bank-Newsletter",
    "broker_agreement_template_available": "Maklervertragsvorlage verfügbar",
    "notes": "Hinweise",
    "no_notes": "Keine Hinweise hinterlegt.",
    "noteType": {
      "absolute_exclusion": "Absoluter Ausschluss",
      "general": "Allgemein",
      "financing": "Finanzierung"
    }
  },
  "en": {
    "master_data": "Master Data",
    "name": "Name",
    "short_name": "Short Name",
    "bic": "BIC",
    "central_phone": "Phone (Central)",
    "valuation_tool": "Valuation Tool",
    "description": "Description",
    "address": "Address",
    "street": "Street",
    "zip_code": "ZIP Code",
    "city": "City",
    "po_box": "PO Box",
    "billing_address": "Billing Address",
    "billing_name": "Recipient",
    "email": "Email",
    "reference_bank": "Reference Bank",
    "reference_bank_name": "Name",
    "bank_code": "Bank Code",
    "bank_codes": "Bank Codes",
    "no_reference_bank": "No reference bank set.",
    "processing_times": "Processing Times",
    "initial_feedback_days": "Initial Feedback",
    "final_commitment_days": "Final Commitment",
    "contract_documents_days": "Contract Documents",
    "days": "days",
    "settings": "Settings",
    "receives_bank_newsletter": "Receives Bank Newsletter",
    "broker_agreement_template_available": "Broker Agreement Template Available",
    "notes": "Notes",
    "no_notes": "No notes set.",
    "noteType": {
      "absolute_exclusion": "Absolute Exclusion",
      "general": "General",
      "financing": "Financing"
    }
  }
}
</i18n>
